import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import { pxToRem } from '@daily/shared/lib/pxToRem';
import {
  DailyVideo,
  useLocalSessionId,
  useMediaTrack,
} from '@daily-co/daily-react';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Tile } from '/components/Tile';
import { TileInfo } from '/components/Tile/TileInfo';
import { EmojiReactions } from '/features/emoji-reactions/components/EmojiReactions';
import { useDisplayName } from '/hooks/useDisplayName';

import { DEFAULT_LANDSCAPE_AR, DEFAULT_PORTRAIT_AR } from './constants';

type Orientation = 'portrait' | 'landscape';

type TileProps = React.ComponentProps<typeof Tile>;
type DailyVideoProps = React.ComponentProps<typeof DailyVideo>;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  isScreen?: boolean;
  network?: TileProps['network'];
  orientation: Orientation;
  sessionId: string;
  showNames?: boolean;
  showReactions?: boolean;
  videoFit?: DailyVideoProps['fit'];
}

export const MobileTile: React.FC<React.PropsWithChildren<Props>> = ({
  isScreen = false,
  network,
  orientation,
  sessionId,
  showNames,
  showReactions,
  videoFit,
  ...props
}) => {
  const { colors } = useTheme();
  const isPortrait = useMemo(() => orientation === 'portrait', [orientation]);
  const [cropVideo, setCropVideo] = useState<Orientation>(null);
  const { zIndex } = useTheme();

  const localSessionId = useLocalSessionId();
  const isLocal = sessionId === localSessionId;

  const hasPoorConnection = network === 'very-low' && isLocal;
  const hasWeakConnection = network === 'low' && isLocal;

  const displayName = useDisplayName(sessionId, showNames, network);

  const noVideoText = useMemo(() => {
    let color: React.ComponentProps<typeof Text>['color'] = 'inherit';
    if (hasWeakConnection) {
      color = 'warning';
    }
    if (hasPoorConnection) {
      color = 'error';
    }
    const style = {
      fontSize: pxToRem(16),
      lineHeight: pxToRem(20),
    };
    return (
      <Text color={color} style={style} truncate variant="strong">
        {displayName}
      </Text>
    );
  }, [displayName, hasPoorConnection, hasWeakConnection]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const videoState = useMediaTrack(
    sessionId,
    isScreen ? 'screenVideo' : 'video'
  );

  /**
   * Handle video resize to determine if video should be cropped based on orientation.
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleVideoResize = () => {
      if (orientation === 'landscape') {
        setCropVideo(null);
        return;
      }
      if (!video) return;
      const { videoHeight, videoWidth } = video;
      if (videoWidth && videoHeight) {
        const aspectRatio = videoWidth / videoHeight;
        if (aspectRatio >= DEFAULT_LANDSCAPE_AR) {
          setCropVideo('landscape');
        } else if (aspectRatio <= DEFAULT_PORTRAIT_AR) {
          setCropVideo('portrait');
        } else {
          setCropVideo(null);
        }
      }
    };
    handleVideoResize();
    video.addEventListener('resize', handleVideoResize);
    return () => {
      video?.removeEventListener('resize', handleVideoResize);
    };
  }, [isPortrait, orientation]);

  return (
    <div
      className={classNames('mobile-tile', {
        cropLandscape: cropVideo === 'landscape',
        cropPortrait: cropVideo === 'portrait',
        portrait: isPortrait,
        landscape: !isPortrait,
      })}
      id={sessionId}
      {...props}
    >
      {showReactions && (
        <div className="mobile-tile-reactions">
          <EmojiReactions animation="fadeIn" sessionIds={[sessionId]} />
        </div>
      )}
      <DailyVideo
        ref={videoRef}
        automirror
        fit={videoFit}
        sessionId={sessionId}
        style={{
          opacity: 0,
        }}
        playableStyle={{
          opacity: 1,
        }}
        type={isScreen ? 'screenVideo' : 'video'}
      />
      {videoState.isOff && !isScreen && (
        <div className="noVideo">{noVideoText}</div>
      )}
      {!isScreen && (
        <TileInfo
          activeIndicator="static"
          name={!videoState.isOff && showNames ? displayName : null}
          sessionId={sessionId}
        />
      )}
      <style jsx>{`
        .mobile-tile {
          background: ${colors.custom.mainAreaBgAccent};
          border-radius: 4px;
          height: 100%;
          overflow: hidden;
          position: relative;
          width: 100%;
          container: mobile-tile / inline-size; // this is for the reaction emojis
        }
        .mobile-tile :global(video) {
          border-radius: 4px;
          height: calc(100% + 4px);
          left: -2px;
          object-position: center;
          position: absolute;
          top: -2px;
          width: calc(100% + 4px);
        }
        .mobile-tile .mobile-tile-reactions {
          bottom: 0;
          left: 0;
          position: absolute;
          z-index: ${zIndex.sideNav};
        }
        .mobile-tile.portrait.cropLandscape :global(video) {
          border-radius: 0;
          // Enforce landscape aspect ratio within portrait container
          height: calc(100% * ${DEFAULT_PORTRAIT_AR / DEFAULT_LANDSCAPE_AR});
          margin-top: calc(
            calc(
                calc(100% / ${DEFAULT_PORTRAIT_AR}) - 100% *
                  ${DEFAULT_PORTRAIT_AR}
              ) / 2
          );
          object-fit: cover;
          width: 100%;
        }
        .mobile-tile.portrait.cropPortrait :global(video) {
          object-fit: cover;
        }
        .noVideo {
          align-items: center;
          background: ${colors.custom.mainAreaBgAccent};
          color: ${colors.custom.mainAreaText};
          display: flex;
          justify-content: center;
          left: 0;
          padding: 8px;
          height: 100%;
          position: absolute;
          top: 0;
          width: 100%;
        }
        .mobile-tile :global(.hand-status) {
          transform: scale(0.75);
          transform-origin: 0 0; // make sure the badge doesn't 'jump around' when the queue number is added
          border-radius: 4px;
        }
      `}</style>
    </div>
  );
};
